import NavigationStyles from '@stories/Widgets/Global/Header/Navigation/Navigation.styles';
import * as React from 'react';
/**
 * Custom hook to manage hover behavior for navigation menus with "hover intent" logic.
 * Prevents accidental menu closing while moving diagonally toward submenus.
 */
export function useHoverIntent() {
    // Store timeouts per item ID so we can cancel them precisely
    const timeoutRefs = React.useRef(new Map());
    // Track recent mouse positions to determine user intent direction
    const mousePositions = React.useRef([]);
    // Track mouse movement globally to build a small history of positions
    React.useEffect(() => {
        const track = (e) => {
            mousePositions.current.push({ x: e.clientX, y: e.clientY });
            if (mousePositions.current.length > 5) {
                mousePositions.current.shift();
            }
        };
        window.addEventListener('mousemove', track);
        return () => window.removeEventListener('mousemove', track);
    }, []);
    /**
     * Handles when mouse enters a navigation or dropdown item.
     * - Clears any pending timeouts
     * - Shows the corresponding dropdown
     * - Optionally closes other open menus
     */
    function handleMouseEnter(event, itemId, type) {
        const item = event.currentTarget;
        const nav = document.querySelector(NavigationStyles.Nav);
        const dropdown = item.querySelector('[data-dropdown="true"]');
        // Cancel any pending close for this item
        const existing = timeoutRefs.current.get(itemId);
        if (existing) {
            clearTimeout(existing);
            timeoutRefs.current.delete(itemId);
        }
        // Always show current item immediately
        if (dropdown) {
            item.setAttribute('aria-expanded', 'true');
            dropdown.setAttribute('data-visible', 'true');
        }
        // Now delay closing others to allow diagonal movement
        if (nav && type === 'nav') {
            setTimeout(() => {
                nav.querySelectorAll('[aria-expanded="true"]').forEach((el) => {
                    if (el !== item)
                        el.setAttribute('aria-expanded', 'false');
                });
                nav.querySelectorAll('[data-dropdown="true"]').forEach((el) => {
                    if (el !== dropdown)
                        el.setAttribute('data-visible', 'false');
                });
            }, 40);
        }
        // Add 'data-overlay' to the header when a navigation item is hovered
        const header = document.querySelector('header');
        if (header) {
            header.setAttribute('data-overlay', 'true');
        }
    }
    /**
     * Handles when mouse leaves a navigation or dropdown item.
     * Starts a timeout that may hide the dropdown depending on pointer trajectory.
     */
    function handleMouseLeave(event, itemId) {
        const item = event.currentTarget;
        const dropdown = item.querySelector('[data-dropdown="true"]');
        if (!dropdown) {
            return;
        }
        const { left, top, bottom } = dropdown.getBoundingClientRect();
        const recent = mousePositions.current[mousePositions.current.length - 1];
        const prev = mousePositions.current[0];
        // Determine if user is moving toward the submenu
        const isMovingTowardSubmenu = prev &&
            recent &&
            prev.x < recent.x &&
            recent.x > left - 30 &&
            recent.y > top - 20 &&
            recent.y < bottom + 20;
        // Set a timeout to close the dropdown only if the user isn't moving toward it
        const timeout = setTimeout(() => {
            if (!isMovingTowardSubmenu) {
                item.setAttribute('aria-expanded', 'false');
                dropdown.setAttribute('data-visible', 'false');
            }
            timeoutRefs.current.delete(itemId);
        }, isMovingTowardSubmenu ? 250 : 0); // Slight delay if movement suggests submenu intent
        timeoutRefs.current.set(itemId, timeout);
    }
    /**
     * Immediately resets all dropdown states and clears timeouts.
     * Useful when the user exits the navigation area completely.
     */
    function resetNavigation() {
        const nav = document.querySelector(NavigationStyles.Nav);
        if (!nav) {
            return;
        }
        // Clear all pending timeouts
        timeoutRefs.current.forEach((timeout) => clearTimeout(timeout));
        timeoutRefs.current.clear();
        // Close any open items
        const expandedElements = nav.querySelectorAll('[aria-expanded="true"]');
        expandedElements.forEach((el) => {
            el.setAttribute('aria-expanded', 'false');
        });
        // Hide all dropdowns
        const allDropdowns = nav.querySelectorAll('[data-dropdown="true"]');
        allDropdowns.forEach((dropdown) => {
            dropdown.setAttribute('data-visible', 'false');
        });
        // Remove 'data-overlay' from the header when the navigation is reset
        const header = document.querySelector('header');
        if (header) {
            header.removeAttribute('data-overlay');
        }
    }
    return { handleMouseEnter, handleMouseLeave, resetNavigation };
}
